import { Tv, Wifi, MonitorPlay, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import Cta from "@/components/Cta";
import { IMAGES } from "@/lib/site";

const STEPS = [
  { icon: Wifi, t: "Get CYAN Internet", d: "Our high-speed network is the best way to stream with no lag or pixelation — even during peak hours." },
  { icon: MonitorPlay, t: "Pick Your Services", d: "Replace expensive cable or build the perfect bundle of the streaming apps you actually watch." },
  { icon: Tv, t: "Find Your Device", d: "Got a Smart TV? You're set. If not, grab an affordable streaming stick and press play." },
];

const BANDWIDTH = [
  { q: "SD Streaming", s: "3 Mbps" },
  { q: "HD Streaming", s: "5–8 Mbps" },
  { q: "4K Ultra HD", s: "25 Mbps" },
  { q: "Multiple 4K screens", s: "50+ Mbps" },
];

export default function Streaming() {
  return (
    <>
      <Seo
        title="Stream Everything — Cut the Cord with CYAN Wireless Internet"
        description="Cut the cord and stream all you want on CYAN Wireless. Fast, unlimited rural internet built for buffer-free HD and 4K streaming across every device in your home."
        path="/streaming"
      />
      <PageHero
        overline="Streaming"
        titleLines={["Cut the cord.", <span key="2">Stream <span className="text-cyan-400 text-glow">everything.</span></span>]}
        subtitle="From live TV to binge-worthy shows, enjoy paying only for the content you love. CYAN's fast speeds deliver the best possible streaming experience — no buffering, no pixelation."
        image={IMAGES.tvBrick}
        imageAlt="TV streaming on a brick wall living room"
      >
        <Cta to="/survey" data-testid="stream-cta">Check Availability <ArrowRight size={16} /></Cta>
      </PageHero>

      <section className="py-24 sm:py-32 bg-ink-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Stagger className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.t} variants={staggerItem} className="bg-ink-900 border border-white/5 rounded-2xl p-8 hover:border-cyan-500/40 transition-colors">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 grid place-items-center rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400"><s.icon size={22} /></div>
                  <span className="font-mono-tech text-cyan-500/50 text-2xl">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-display font-bold text-xl text-white mb-2">{s.t}</h3>
                <p className="text-zinc-400 leading-relaxed text-[15px]">{s.d}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="pb-24 sm:pb-32 bg-ink-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative rounded-2xl overflow-hidden border border-white/10">
            <img src={IMAGES.familyMovie} alt="Family enjoying a movie night" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 to-transparent" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline mb-4">How much speed?</p>
            <h2 className="font-display font-extrabold text-4xl tracking-tighter text-white leading-[0.95] mb-8">Stream all you want.</h2>
            <div className="divide-y divide-white/10 border-y border-white/10">
              {BANDWIDTH.map((b) => (
                <div key={b.q} className="flex items-center justify-between py-4">
                  <span className="text-white">{b.q}</span>
                  <span className="font-mono-tech text-cyan-400">{b.s}</span>
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-sm mt-6">With unlimited data and fast speeds, CYAN keeps every screen in the house running smoothly at once.</p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
